import React from 'react';
import { FaCheckCircle, FaUndo } from 'react-icons/fa';
import Modal from './Modal';
import VoucherStatusNotesForm from './VoucherStatusNotesForm';
import { Badge, Button } from '../shared/ui';
import styles from './VoucherStudentWorkspaceModal.module.css';

function fmtMoney(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return `$${x.toFixed(2)}`;
}

function badgeVariant(status) {
  switch (status) {
    case 'complete':
      return 'success';
    case 'partial':
      return 'warning';
    case 'missing':
      return 'danger';
    case 'overpaid':
      return 'info';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export default function VoucherStudentWorkspaceModal({
  open,
  onClose,
  student,
  settlementDateId,
  notesCount = 0,
  closureLoading,
  onMarkClosed,
  onReopen,
  onNotesChange
}) {
  if (!student) return null;

  const gap = Number(student.expected) - Number(student.actual);
  const gapCls =
    gap > 0 ? styles.metricValueDanger : gap < 0 ? styles.metricValueSuccess : '';

  const introItems = [];
  if (student.family_name) introItems.push({ label: 'Family', value: student.family_name });
  if (student.dob)
    introItems.push({
      label: 'Date of birth',
      value: new Date(student.dob).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    });
  if (student.location_address) introItems.push({ label: 'Location', value: student.location_address });

  const showResolve = student.status === 'missing' || student.status === 'partial';
  const showReopen = student.status === 'closed';

  return (
    <Modal open={open} onClose={onClose} title={student.name || 'Student'} size="xxl" bodyFill>
      <div className={styles.modalStack}>
        {introItems.length > 0 ? (
          <div className={styles.intro} role="region" aria-label="Student details">
            {introItems.map((item) => (
              <div key={item.label} className={styles.introItem}>
                <span className={styles.introLabel}>{item.label}</span>
                <span className={styles.introValue}>{item.value}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className={styles.metrics}>
          <div className={styles.metric}>
            <p className={styles.metricLabel}>Expected</p>
            <p className={styles.metricValue}>{fmtMoney(student.expected)}</p>
          </div>
          <div className={styles.metric}>
            <p className={styles.metricLabel}>Received</p>
            <p className={`${styles.metricValue} ${styles.metricValueAccent}`}>{fmtMoney(student.actual)}</p>
          </div>
          <div className={styles.metric}>
            <p className={styles.metricLabel}>Gap</p>
            <p className={`${styles.metricValue} ${gapCls}`}>{fmtMoney(gap)}</p>
          </div>
          <div className={styles.metric}>
            <p className={styles.metricLabel}>Notes</p>
            <p className={styles.metricValue}>{notesCount}</p>
          </div>
        </div>

        <div className={styles.statusRow}>
          <div className={styles.statusMain}>
            <span className={styles.statusLabel}>Status</span>
            <Badge variant={badgeVariant(student.status)}>
              {student.status.charAt(0).toUpperCase() + student.status.slice(1)}
            </Badge>
          </div>
          <div className={styles.statusActions}>
            {closureLoading ? (
              <span className={styles.actionBusy}>Updating…</span>
            ) : (
              <>
                {showReopen ? (
                  <Button variant="secondary" size="sm" iconLeft={<FaUndo />} onClick={onReopen}>
                    Reopen issue
                  </Button>
                ) : null}
                {showResolve ? (
                  <Button variant="secondary" size="sm" iconLeft={<FaCheckCircle />} onClick={onMarkClosed}>
                    Mark issue resolved
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className={styles.grid}>
          <div className={styles.mainColumn}>
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Expected by day</h3>
              <div className={styles.scrollTable}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Age (mo)</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {student.detailRows && student.detailRows.length > 0 ? (
                      student.detailRows.map((row, i) => (
                        <tr key={i}>
                          <td>{row.date}</td>
                          <td>{row.day_name}</td>
                          <td>{row.age_months}</td>
                          <td>${Number(row.rate).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} style={{ padding: 'var(--s-4)', color: 'var(--c-text-muted)' }}>
                          No day breakdown for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {student.payments && student.payments.length > 0 ? (
              <div className={styles.panel}>
                <h3 className={styles.panelTitle}>Imported payments</h3>
                <div className={styles.scrollTable}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Agreement #</th>
                        <th>Total</th>
                        <th>Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {student.payments.map((p, i) => (
                        <tr key={i}>
                          <td>{p.agreement_number}</td>
                          <td>${Number(p.total).toFixed(2)}</td>
                          <td>{student.date_range || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>

          <div className={`${styles.panel} ${styles.notesPanel}`}>
            <div className={styles.panelTitleRow}>
              <h3 className={styles.panelTitleFlush} id="voucher-modal-notes-heading">
                Notes
              </h3>
              <span className={styles.countPill}>
                {notesCount === 1 ? '1 note' : `${notesCount} notes`}
              </span>
            </div>
            <div className={styles.notesPanelBody} aria-labelledby="voucher-modal-notes-heading">
              {settlementDateId ? (
                <VoucherStatusNotesForm
                  key={`${student.student_id}-${settlementDateId}`}
                  studentId={student.student_id}
                  settlementDateId={settlementDateId}
                  hideHeading
                  onNotesChange={onNotesChange}
                />
              ) : (
                <p className={styles.meta}>Select a settlement period to add notes.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
